import { useState } from 'react';
import { InputNumberProps } from 'antd';
import { useParams } from 'react-router-dom';
import { showDefaultErrorToast, useDebounce } from '@app/utils';
import {
  useMergeQuestions,
  useOptimisticMergeQuestionWithSimilarQuestion,
  useQuestionDetails,
  useQuestionsOnMutate,
  useSimilarQuestions,
} from '@data/hooks';
import { QuestionView } from '@data/sdk/match';

const LIMIT = 3;

export const useQuestionsMerge = () => {
  // this is UI value
  const [sliderValue, setSliderValue] = useState(0.85);
  const [searchString, setSearchString] = useState('');

  const [questions, setQuestions] = useState<QuestionView[]>([]);

  const { questionId = '' } = useParams<{ questionId: string }>();

  const { data: question, isLoading: isQuestionLoading } = useQuestionDetails(questionId);

  const debouncedSliderValue = useDebounce(sliderValue);

  // this is real value
  const realSliderValue = Number((1 - debouncedSliderValue).toFixed(2));

  const { mutate: mergeQuestionWithSimilarQuestion } =
    useOptimisticMergeQuestionWithSimilarQuestion();
  const { mutate: mergeQuestions } = useMergeQuestions();
  const { mutate: getQuestions, isPending: isQuestionsLoading } = useQuestionsOnMutate();

  const { data: similarQuestions, isLoading: isSimilarQuestionsLoading } = useSimilarQuestions(
    questionId,
    false,
    { limit: LIMIT, offset: 0 },
  );

  const handleQuestionsSearch = (value: string) => {
    getQuestions(
      {
        params: {
          getQuestionsBody: {
            limit: LIMIT,
            offset: 0,
            searchString: value,
            currentVersionOnly: false,
          },
        },
      },
      {
        onSuccess: (data) => {
          setQuestions(data.items);
        },
      },
    );
  };

  const handleChange = async ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(value);

    if (!value) {
      setQuestions([]);
    } else handleQuestionsSearch(value);
  };

  const handleQuestionWithSimilarQuestionMerge = (id: string) => {
    mergeQuestionWithSimilarQuestion(
      {
        id,
        values: { targetQuestionId: questionId },
      },
      {
        onError: showDefaultErrorToast,
      },
    );
  };

  const handleQuestionsMerge = (id: string) => {
    // optimistic update
    const oldQuestions = questions;
    const newQuestions = questions.filter((q) => q.id !== id);
    setQuestions(newQuestions);

    mergeQuestions(
      { id, values: { targetQuestionId: questionId } },
      {
        onError: (err) => {
          showDefaultErrorToast(err);
          setQuestions(oldQuestions);
        },
        onSuccess: () => {
          handleQuestionsSearch(searchString);
        },
      },
    );
  };

  const handleSliderChange: InputNumberProps['onChange'] = (value) => {
    if (Number.isNaN(value)) {
      return;
    }

    setSliderValue(value as number);
  };

  return {
    question,
    sliderValue,
    searchString,
    isQuestionLoading,
    debouncedSliderValue: realSliderValue,
    isQuestionsLoading: isQuestionsLoading || isSimilarQuestionsLoading,
    questionsToShow: searchString ? questions : similarQuestions?.items ?? [],
    handleChange,
    handleSliderChange,
    handleQuestionsMerge: searchString
      ? handleQuestionsMerge
      : handleQuestionWithSimilarQuestionMerge,
  };
};
